import type { FeatureCollection, Point } from 'geojson'
import mapboxgl, { type Map as MapboxMap } from 'mapbox-gl'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Layer, Source } from 'react-map-gl'
import { useSuppressNotificationSandwich } from 'hooks/useSuppressNotificationSandwich'
import { NOTIFICATION_SANDWICH_SUPPRESS } from 'utils/notificationSandwichGate'
import { getBeforeIdUnderRobotMarkers } from 'utils/mapUtils'

import { buildServiceCalibMapFeatures } from './serviceCalibMapFeatures'
import {
  SERVICE_CALIB_ICON,
  SERVICE_CALIB_MAP_ICON_SIZE_LAYOUT,
  SERVICE_CALIB_MAP_PIN_OPACITY,
  SERVICE_CALIB_PIN_IMAGES,
  loadServiceCalibPinImages,
  registerServiceCalibStyleImageMissingHandler,
} from './serviceCalibMapIcons'
import type { ServiceCalibCatalogItem } from './serviceCalibTypes'

import './ServiceCalibMap.module.css'

const emptyFc: FeatureCollection<Point> = { type: 'FeatureCollection', features: [] }

/** Cluster when pins overlap; between legacy (14/50) and aggressive (18/88). */
const CLUSTER_MAX_ZOOM = 16
const CLUSTER_RADIUS = 69

const SERVICE_CALIB_PIN_LAYERS = ['service-calib-pins', 'service-calib-cluster-circle'] as const

const layerClusterCircle = {
  id: 'service-calib-cluster-circle',
  type: 'circle' as const,
  filter: ['has', 'point_count'],
  paint: {
    'circle-color': '#2563eb',
    'circle-radius': ['step', ['get', 'point_count'], 20, 10, 26, 30, 32] as mapboxgl.Expression,
    'circle-stroke-width': 2,
    'circle-stroke-color': 'rgba(255, 255, 255, 0.45)',
  },
}

const layerClusterCount = {
  id: 'service-calib-cluster-count',
  type: 'symbol' as const,
  filter: ['has', 'point_count'],
  layout: {
    'text-field': '{point_count_abbreviated}',
    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
    'text-size': 14,
    'text-allow-overlap': true,
  },
  paint: {
    'text-color': '#ffffff',
  },
}

const ServiceCalibMap = ({
  map,
  items,
  selectedKey,
  onSelectKey,
}: {
  map: MapboxMap | undefined
  items: ServiceCalibCatalogItem[]
  selectedKey: string | null
  onSelectKey: (key: string | null) => void
}) => {
  const [imagesReady, setImagesReady] = useState(false)
  const [styleEpoch, setStyleEpoch] = useState(0)
  const [beforeId, setBeforeId] = useState<string | undefined>()
  const hoverPopupRef = useRef<mapboxgl.Popup | null>(null)
  useSuppressNotificationSandwich(Boolean(map), NOTIFICATION_SANDWICH_SUPPRESS.SERVICE_CALIB_MAP)

  const data = useMemo(() => buildServiceCalibMapFeatures(items, selectedKey), [items, selectedKey])

  useEffect(() => {
    if (!map) {
      setBeforeId(undefined)
      return
    }
    const syncBeforeId = () => {
      setBeforeId(prev => {
        const next = getBeforeIdUnderRobotMarkers(map)
        return prev === next ? prev : next
      })
    }
    syncBeforeId()
    map.on('idle', syncBeforeId)
    map.on('style.load', syncBeforeId)
    return () => {
      map.off('idle', syncBeforeId)
      map.off('style.load', syncBeforeId)
    }
  }, [map, styleEpoch, imagesReady])

  useEffect(() => {
    if (!map) {
      setImagesReady(false)
      return
    }

    let cancelled = false

    const unregisterMissing = registerServiceCalibStyleImageMissingHandler(map)
    let idleHandler: (() => void) | undefined
    const detachIdle = () => {
      if (idleHandler) {
        map.off('idle', idleHandler)
        idleHandler = undefined
      }
    }

    const onStyleLoad = () => {
      detachIdle()
      setImagesReady(false)
      void loadServiceCalibPinImages(map).then(() => {
        if (cancelled) {
          return
        }
        const hasPins = SERVICE_CALIB_PIN_IMAGES.some(({ name }) => map.hasImage(name))
        const hasStation = map.hasImage(SERVICE_CALIB_ICON.STATION)
        setImagesReady(hasPins || hasStation)
        if (!hasPins && !hasStation) {
          console.warn('[ServiceCalibMap] no pin images registered after load')
        }
        setStyleEpoch(epoch => epoch + 1)
        try {
          map.triggerRepaint()
        } catch {
          /* ignore */
        }
      })
    }

    const trySyncWhenReady = () => {
      if (!map.isStyleLoaded()) {
        return false
      }
      onStyleLoad()
      return true
    }

    map.on('style.load', onStyleLoad)
    if (!trySyncWhenReady()) {
      idleHandler = () => {
        if (cancelled) {
          return
        }
        if (trySyncWhenReady()) {
          detachIdle()
        }
      }
      map.on('idle', idleHandler)
    }

    return () => {
      cancelled = true
      detachIdle()
      unregisterMissing()
      map.off('style.load', onStyleLoad)
      setImagesReady(false)
    }
  }, [map])

  const onPinClicked = useCallback(
    (e: { features?: Array<{ properties?: { key?: string } | null }> }) => {
      const key = e.features?.[0]?.properties?.key
      if (key) {
        onSelectKey(key)
      }
    },
    [onSelectKey]
  )

  const onClusterClicked = useCallback(
    (e: {
      features?: Array<{
        properties?: { cluster_id?: number }
        geometry?: { coordinates?: [number, number] }
      }>
    }) => {
      if (!map || !e.features?.[0]) {
        return
      }
      const feature = e.features[0]
      const clusterId = feature.properties?.cluster_id
      if (clusterId === undefined) {
        return
      }
      const source = map.getSource('service-calib-pins') as mapboxgl.GeoJSONSource & {
        getClusterExpansionZoom?: (clusterId: number, callback: (err: Error | null, zoom: number) => void) => void
      }
      source?.getClusterExpansionZoom?.(clusterId, (err, zoom) => {
        if (err || zoom == null || !feature.geometry?.coordinates) {
          return
        }
        map.easeTo({
          center: feature.geometry.coordinates as [number, number],
          zoom,
          duration: 400,
        })
      })
    },
    [map]
  )

  useEffect(() => {
    if (!map || data.features.length === 0) {
      return
    }

    const onMapBackgroundClick = (e: mapboxgl.MapMouseEvent) => {
      const layers = SERVICE_CALIB_PIN_LAYERS.filter(id => Boolean(map.getLayer(id)))
      if (!layers.length) {
        return
      }
      const hit = map.queryRenderedFeatures(e.point, { layers: [...layers] })
      if (hit.length === 0) {
        onSelectKey(null)
      }
    }

    map.on('click', 'service-calib-pins', onPinClicked as never)
    map.on('click', 'service-calib-cluster-circle', onClusterClicked as never)
    map.on('click', onMapBackgroundClick)

    const setPointer = () => {
      map.getCanvas().style.cursor = 'pointer'
    }
    const resetPointer = () => {
      map.getCanvas().style.cursor = ''
    }

    const showHoverName = (e: mapboxgl.MapLayerMouseEvent) => {
      map.getCanvas().style.cursor = 'pointer'
      const feature = e.features?.[0]
      const name = feature?.properties?.name
      const coords = (feature?.geometry as GeoJSON.Point | undefined)?.coordinates
      if (!name || !coords || coords.length < 2) {
        return
      }
      if (!hoverPopupRef.current) {
        hoverPopupRef.current = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
          offset: 18,
          className: 'service-calib-pin-tooltip',
        })
      }
      hoverPopupRef.current.setLngLat([coords[0], coords[1]]).setText(String(name)).addTo(map)
    }
    const hideHoverName = () => {
      map.getCanvas().style.cursor = ''
      hoverPopupRef.current?.remove()
    }

    map.on('mouseenter', 'service-calib-pins', showHoverName as never)
    map.on('mouseleave', 'service-calib-pins', hideHoverName)
    map.on('mouseenter', 'service-calib-cluster-circle', setPointer)
    map.on('mouseleave', 'service-calib-cluster-circle', resetPointer)

    return () => {
      map.off('click', 'service-calib-pins', onPinClicked as never)
      map.off('click', 'service-calib-cluster-circle', onClusterClicked as never)
      map.off('click', onMapBackgroundClick)
      map.off('mouseenter', 'service-calib-pins', showHoverName as never)
      map.off('mouseleave', 'service-calib-pins', hideHoverName)
      map.off('mouseenter', 'service-calib-cluster-circle', setPointer)
      map.off('mouseleave', 'service-calib-cluster-circle', resetPointer)
      hoverPopupRef.current?.remove()
      hoverPopupRef.current = null
    }
  }, [map, data.features.length, onPinClicked, onClusterClicked, onSelectKey, styleEpoch])

  if (data.features.length === 0 || !map || !imagesReady) {
    return null
  }

  return (
    <Source
      key={`service-calib-pins-${styleEpoch}`}
      id="service-calib-pins"
      type="geojson"
      data={data.features.length ? data : emptyFc}
      cluster
      clusterMaxZoom={CLUSTER_MAX_ZOOM}
      clusterRadius={CLUSTER_RADIUS}
    >
      <Layer {...layerClusterCircle} source="service-calib-pins" beforeId={beforeId} />
      <Layer {...layerClusterCount} source="service-calib-pins" beforeId={beforeId} />
      <Layer
        id="service-calib-pins"
        source="service-calib-pins"
        type="symbol"
        filter={['!', ['has', 'point_count']]}
        beforeId={beforeId}
        layout={{
          'icon-image': ['get', 'iconId'],
          'icon-anchor': 'center',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-size': SERVICE_CALIB_MAP_ICON_SIZE_LAYOUT,
        }}
        paint={{
          'icon-opacity': SERVICE_CALIB_MAP_PIN_OPACITY,
        }}
      />
    </Source>
  )
}

export default ServiceCalibMap
